import React, { useEffect, useMemo, useState } from 'react';
import GlobalWrapper from '../../../components/Wrapper/GlobalWrapper';
import { Autocomplete, Box, Stack, TextField } from '@mui/material';
import PlacementCards from './components/PlacementCards';
import FromAndToDatePicker from '../../../components/mui/FromAndToDatePicker';
import GlobalSelectFieldWithoutFormik from '../../../components/mui/GlobalSelectFieldWithoutFormik';
import { allProjects } from './utils/Constaints';
import GlobalTable from '../../../components/CustomTableWithPagination/GlobalTable';
import { analyticsTableHeader } from './utils/analyticTableHeader';
import AnalyticChart from './components/AnalyticChart';
import { useDispatch } from 'react-redux';
import {
  getPlacementTrackerAnalyticsTable,
  getPlacementTrackerAnalyticsTableCards,
  getPlacementTrackerAnalyticsTableGraph,
} from '../../../redux/features/generalSlice';
import { useSelector } from 'react-redux';
import { PLACEMENT_TYPES } from '../placementTrackerTable/utils/placementTypes';
import dayjs from 'dayjs';
import { getAllBatchNameNoPagination } from '../../../redux/features/batchSlice';

export default function PlacementTrackerDashboard() {
  const [fromDate, setFromDate] = useState(dayjs().startOf('month').format('YYYY-MM-DD'));
  const [toDate, setToDate] = useState(dayjs().endOf('month').format('YYYY-MM-DD'));
  const [status, setStatus] = useState({ id: '', value: 'All' });
  const [batchId, setBatchId] = useState(null);
  const [project, setProject] = useState({ id: '', value: '' });
  const dispatch = useDispatch();

  const placementTrackerAnalyticsTable = useSelector((state) => state.general.placementTrackerAnalyticsTable.data);
  const placementTrackerAnalyticsTableLoading = useSelector(
    (state) => state.general.placementTrackerAnalyticsTable.loading,
  );
  const batchesAll = useSelector((state) => state.batch.batchesAll);
  const batchesAllLoading = useSelector((state) => state.batch.loading);

  const meta = useSelector((state) => state.general.placementTrackerAnalyticsTable.meta);
  const [tableState, setTableState] = useState({
    page: 1,
    pageSize: 50,
    search: '',
    sort: { field: '', direction: '' },
    frozenColumns: [],
    checkedRowIds: [],
    hiddenColumns: [],
  });

  const dispatchParams = useMemo(
    () => ({
      page: tableState?.page || 1,
      limit: tableState?.pageSize || 50,
      search: tableState?.search,
      status: status?.value === 'All' ? '' : status?.value,
      projectId: project?.id === -1 ? '' : project?.id,
      batchId: batchId || '',
      fromDate,
      toDate,
    }),
    [tableState?.page, tableState?.pageSize, tableState?.search, status, project, fromDate, toDate, batchId],
  );

  useEffect(() => {
    dispatch(getAllBatchNameNoPagination({ sAllBatchesNeeded: true }));
  }, [dispatch]);

  useEffect(() => {
    dispatch(getPlacementTrackerAnalyticsTable(dispatchParams));
  }, [dispatch, dispatchParams]);

  // cards
  useEffect(() => {
    dispatch(getPlacementTrackerAnalyticsTableCards({ batchId: batchId || '' }));
    dispatch(getPlacementTrackerAnalyticsTableGraph({ batchId: batchId || '', isBatch: true }));
  }, [dispatch, batchId]);

  return (
    <GlobalWrapper
      title={'Placement Tracker Dashboard'}
      actions={
        <Autocomplete
          getOptionLabel={(opt) => opt?.course_batch_name || ''}
          renderOption={(props, opt) => (
            <li {...props} key={opt?.course_batch_id} value={opt?.course_batch_id}>
              {opt?.course_batch_name}
            </li>
          )}
          isOptionEqualToValue={(opt, val) => opt?.course_batch_id === val?.course_batch_id}
          options={batchesAll}
          onChange={(_, v) => setBatchId(v?.course_batch_id || null)}
          renderInput={(params) => <TextField {...params} label="Batch" />}
          sx={{ minWidth: { xs: 200, sm: 300 } }}
          loading={batchesAllLoading}
        />
      }
    >
      <Stack gap={3}>
        {/* Need to memo */}
        <PlacementCards />
        <Box display={'flex'} gap={1} sx={{ flexDirection: { xs: 'column', md: 'row' } }}>
          <FromAndToDatePicker
            toDate={toDate}
            setToDate={setToDate}
            fromDate={fromDate}
            setFromDate={setFromDate}
            fullWidth={true}
          />
          <Box display={'flex'} gap={1}>
            <GlobalSelectFieldWithoutFormik
              data={allProjects}
              keys={{
                itemId: 'id',
                itemValue: 'label',
              }}
              label={'Project'}
              selectedDetails={project}
              setChange={setProject}
              sx={{ minWidth: { xs: 150, sm: 200 }, flex: 1 }}
            />
            <GlobalSelectFieldWithoutFormik
              data={[{ id: 0, label: 'All' }, { id: -1, label: 'pending' }, ...PLACEMENT_TYPES]}
              keys={{
                itemId: 'id',
                itemValue: 'label',
              }}
              label={'Status'}
              selectedDetails={status}
              setChange={setStatus}
              sx={{ minWidth: { xs: 150, sm: 200 }, flex: 1 }}
            />
          </Box>
        </Box>
        <GlobalTable
          headers={analyticsTableHeader}
          rowUniqueId="slNo"
          rows={placementTrackerAnalyticsTable}
          tableState={tableState}
          setTableState={setTableState}
          loading={placementTrackerAnalyticsTableLoading}
          meta={meta}
        />
        <AnalyticChart />
      </Stack>
    </GlobalWrapper>
  );
}
